import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Cpu, HardDrive, MemoryStick, ShieldAlert } from 'lucide-react'
import { NOTICE_HTML_CLASSES, toSafeNoticeHtml } from '@/lib/noticeHtml'
import { cn } from '@/lib/utils'
import type { OSImageFormData } from '@/schemas/adminOSImage.schema'

interface OSImagePreviewProps {
  formData: Partial<OSImageFormData>
}

export function OSImagePreview({ formData }: OSImagePreviewProps) {
  const {
    name,
    version,
    category,
    iconUrl,
    isFeatured,
    minimumSpecs,
    licenseActivationSupported,
    licenseWarningMessage,
  } = formData

  // Mirrors the customer wizard: the notice appears only for a Windows image marked unsupported
  // that carries a message, rendered through the same sanitiser the client console uses.
  const licenseNotice =
    category === 'Windows' && licenseActivationSupported === false
      ? licenseWarningMessage?.trim()
      : undefined

  return (
    <Card className="shadow-none">
      <CardHeader className="px-4 pb-3 pt-4">
        <CardTitle className="text-base">Client Portal Preview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 px-4 pb-4 pt-0">
        {/* OS Image Card Preview. The description is deliberately not shown: it is a paragraph the
            operator is already editing in the form, and repeating it here filled the panel without
            telling them anything new. */}
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={name || 'OS Image'}
                className="h-12 w-12 shrink-0 rounded object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-muted">
                <span className="text-sm font-medium">
                  {name ? name.substring(0, 2).toUpperCase() : 'OS'}
                </span>
              </div>
            )}

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-semibold">{name || 'OS Image Name'}</h4>
                {isFeatured && (
                  <Badge variant="secondary" className="text-xs">
                    Featured
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {version ? `Version ${version}` : 'Version'} • {category || 'Category'}
              </p>
            </div>
          </div>

          {/* Minimum Specs — full card width so the three values get equal columns */}
          {minimumSpecs && (
            <div className="mt-3 grid grid-cols-3 gap-2 rounded-md bg-muted/50 p-2 text-xs">
              <div className="flex items-center gap-1.5">
                <Cpu className="h-3 w-3 text-muted-foreground" />
                <span>{minimumSpecs.cpu || 1} CPU</span>
              </div>
              <div className="flex items-center gap-1.5">
                <MemoryStick className="h-3 w-3 text-muted-foreground" />
                <span>
                  {minimumSpecs.memory
                    ? minimumSpecs.memory >= 1024
                      ? `${(minimumSpecs.memory / 1024).toFixed(1)}GB`
                      : `${minimumSpecs.memory}MB`
                    : '1GB'}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <HardDrive className="h-3 w-3 text-muted-foreground" />
                <span>{minimumSpecs.disk || 20}GB</span>
              </div>
            </div>
          )}
        </div>

        {licenseNotice && (
          <div className="flex items-start gap-2.5 rounded-lg bg-warn-soft p-3">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warn-ink" />
            <div
              className={cn('min-w-0 flex-1 text-xs leading-relaxed', NOTICE_HTML_CLASSES)}
              // Operator-authored HTML, sanitised to a tag allowlist by toSafeNoticeHtml.
              dangerouslySetInnerHTML={{ __html: toSafeNoticeHtml(licenseNotice) }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
